import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, Upload, X, Building2, BookMarked, Save, Trash2 } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useLang } from "@/context/LanguageContext";

const STORAGE_KEY = "riclassificatore.cover.v1";
const TPL_KEY = "riclassificatore.templates.v2";

const emptyCover = {
  company_name: "",
  period_from: "",
  period_to: "",
  generated_at: "",
  vat: "",
  cf: "",
  cciaa: "",
  address: "",
  phone: "",
  email: "",
  website: "",
  logo_b64: "",
};

// Fields that belong to the STUDIO template (not client-specific).
// Client-specific fields (company_name, VAT, CF, CCIAA, periods, generated_at) are excluded.
const TEMPLATE_COVER_KEYS = ["logo_b64", "address", "phone", "email", "website"];

const loadCover = () => {
  try {
    const s = localStorage.getItem(STORAGE_KEY);
    return s ? { ...emptyCover, ...JSON.parse(s) } : { ...emptyCover };
  } catch {
    return { ...emptyCover };
  }
};

const loadTemplates = () => {
  try {
    const s = localStorage.getItem(TPL_KEY);
    const arr = s ? JSON.parse(s) : [];
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
};

const saveTemplates = (list) => {
  try { localStorage.setItem(TPL_KEY, JSON.stringify(list)); } catch {}
};

const uuid = () => {
  try { return crypto.randomUUID(); } catch { return String(Date.now()) + Math.random().toString(36).slice(2); }
};

export default function CoverLetterForm({ cover, onChange, sector = "generico", onSectorApply }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [activeTplId, setActiveTplId] = useState("");
  const fileRef = useRef(null);

  const seededRef = useRef(false);

  useEffect(() => {
    if (seededRef.current) return;
    seededRef.current = true;
    // On mount, seed from localStorage if empty
    const isEmpty = Object.values(cover || {}).every((v) => !v);
    if (isEmpty) {
      const saved = loadCover();
      if (Object.values(saved).some((v) => v)) {
        onChange(saved);
        setOpen(true);
      }
    }
    setTemplates(loadTemplates());
  }, [cover, onChange]);

  const update = (key, value) => {
    const next = { ...cover, [key]: value };
    onChange(next);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch {}
  };

  const handleLogo = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => update("logo_b64", reader.result);
    reader.readAsDataURL(file);
  };

  const hasData = Object.values(cover || {}).some((v) => v);

  const activeTpl = useMemo(() => templates.find((tpl) => tpl.id === activeTplId), [templates, activeTplId]);

  const applyTemplate = (id) => {
    const tpl = templates.find((tp) => tp.id === id);
    if (!tpl) return;
    setActiveTplId(id);
    // Merge template studio fields into current cover (do NOT overwrite client-specific values that are already set)
    const merged = { ...cover };
    for (const k of TEMPLATE_COVER_KEYS) {
      if (tpl.cover?.[k]) merged[k] = tpl.cover[k];
    }
    onChange(merged);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(merged)); } catch {}
    if (tpl.sector && onSectorApply) onSectorApply(tpl.sector);
    setOpen(true);
    toast.success(`${t("tpl_loaded")}: ${tpl.name}`);
  };

  const saveAsNew = () => {
    const name = window.prompt(t("tpl_prompt_name"));
    if (!name || !name.trim()) return;
    const tplCover = {};
    for (const k of TEMPLATE_COVER_KEYS) tplCover[k] = cover?.[k] || "";
    const newTpl = {
      id: uuid(),
      name: name.trim(),
      cover: tplCover,
      sector,
      createdAt: new Date().toISOString(),
    };
    const next = [...templates, newTpl];
    setTemplates(next);
    saveTemplates(next);
    setActiveTplId(newTpl.id);
    toast.success(`${t("tpl_saved")}: ${newTpl.name}`);
  };

  const updateActive = () => {
    if (!activeTpl) return;
    const tplCover = {};
    for (const k of TEMPLATE_COVER_KEYS) tplCover[k] = cover?.[k] || "";
    const next = templates.map((tp) => tp.id === activeTpl.id
      ? { ...tp, cover: tplCover, sector, updatedAt: new Date().toISOString() }
      : tp);
    setTemplates(next);
    saveTemplates(next);
    toast.success(`${t("tpl_updated")}: ${activeTpl.name}`);
  };

  const deleteActive = () => {
    if (!activeTpl) return;
    if (!window.confirm(`${t("tpl_delete")} "${activeTpl.name}" ?`)) return;
    const next = templates.filter((tp) => tp.id !== activeTpl.id);
    setTemplates(next);
    saveTemplates(next);
    setActiveTplId("");
    toast.success(t("tpl_deleted"));
  };

  const field = (key, label, type = "text", extraProps = {}) => (
    <div className="space-y-1.5">
      <Label htmlFor={`cover-${key}`} className="text-xs font-medium text-slate-600">{label}</Label>
      <Input
        id={`cover-${key}`}
        data-testid={`cover-${key}`}
        type={type}
        value={cover?.[key] || ""}
        onChange={(e) => update(key, e.target.value)}
        className="h-9 bg-white"
        {...extraProps}
      />
    </div>
  );

  return (
    <div className="mb-6 rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="cover-letter-form">
      <button
        type="button"
        data-testid="cover-toggle"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-3 rounded-t-xl px-5 py-4 text-left transition-colors hover:bg-slate-50"
      >
        <div className="grid h-9 w-9 place-items-center rounded-md bg-blue-50 text-blue-700">
          <Building2 className="h-4 w-4" strokeWidth={2} />
        </div>
        <div className="flex-1">
          <div className="font-heading text-sm font-semibold text-slate-900">
            {t("cover_title")}
          </div>
          <div className="text-xs text-slate-500">
            {hasData ? (cover.company_name || t("cover_filled")) : t("cover_subtitle_hint")}
          </div>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
      </button>

      {open && (
        <>
          {/* Template selector */}
          <div className="border-t border-slate-100 bg-slate-50/60 px-5 py-4" data-testid="template-selector">
            <div className="flex items-start gap-3">
              <div className="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-white text-purple-700 ring-1 ring-purple-200">
                <BookMarked className="h-4 w-4" strokeWidth={2} />
              </div>
              <div className="flex-1">
                <div className="text-sm font-medium text-slate-900">{t("tpl_title")}</div>
                <div className="text-xs text-slate-500">{t("tpl_hint")}</div>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <div className="min-w-[200px] flex-1">
                    <Select value={activeTplId || undefined} onValueChange={applyTemplate}>
                      <SelectTrigger data-testid="template-select" className="h-9 bg-white">
                        <SelectValue placeholder={templates.length ? t("tpl_select_placeholder") : t("tpl_no_templates")} />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        {templates.length === 0 ? (
                          <div className="px-3 py-2 text-xs text-slate-400">{t("tpl_no_templates")}</div>
                        ) : templates.map((tp) => (
                          <SelectItem key={tp.id} value={tp.id} data-testid={`template-item-${tp.id}`}>
                            {tp.name} · {tp.sector || "generico"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid="template-save-new-btn"
                    onClick={saveAsNew}
                    className="h-9 rounded-full border-slate-300"
                  >
                    <Save className="mr-1.5 h-3.5 w-3.5" strokeWidth={2} />
                    {t("tpl_save_new")}
                  </Button>
                  {activeTpl && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        data-testid="template-update-btn"
                        onClick={updateActive}
                        className="h-9 rounded-full border-slate-300"
                      >
                        {t("tpl_update")}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid="template-delete-btn"
                        onClick={deleteActive}
                        className="h-9 rounded-full text-red-600 hover:bg-red-50 hover:text-red-700"
                      >
                        <Trash2 className="mr-1.5 h-3.5 w-3.5" strokeWidth={2} />
                        {t("tpl_delete")}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="grid gap-4 border-t border-slate-100 p-5 sm:grid-cols-2">
            {/* Logo uploader */}
            <div className="sm:col-span-2">
              <Label className="text-xs font-medium text-slate-600">{t("cover_logo")}</Label>
              <div className="mt-1.5 flex items-center gap-4 rounded-lg border border-dashed border-slate-300 p-4">
                {cover?.logo_b64 ? (
                  <div className="flex items-center gap-3">
                    <img src={cover.logo_b64} alt="logo" className="h-16 w-auto rounded border border-slate-200 bg-slate-50 object-contain p-1" data-testid="cover-logo-preview" />
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid="cover-logo-remove"
                      onClick={() => update("logo_b64", "")}
                      className="h-8 text-xs text-slate-500 hover:text-red-600"
                    >
                      <X className="mr-1 h-3.5 w-3.5" /> {t("cover_logo_remove")}
                    </Button>
                  </div>
                ) : (
                  <>
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/svg+xml"
                      className="hidden"
                      data-testid="cover-logo-input"
                      onChange={(e) => handleLogo(e.target.files?.[0])}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid="cover-logo-upload"
                      onClick={() => fileRef.current?.click()}
                      className="h-9 rounded-full border-slate-300"
                    >
                      <Upload className="mr-2 h-4 w-4" /> {t("cover_logo_upload")}
                    </Button>
                    <div className="text-xs text-slate-500">{t("cover_logo_hint")}</div>
                  </>
                )}
              </div>
            </div>

            <div className="sm:col-span-2">{field("company_name", t("cover_company_name"))}</div>
            <div>{field("period_from", t("cover_period_from"), "date")}</div>
            <div>{field("period_to", t("cover_period_to"), "date")}</div>
            <div className="sm:col-span-2">{field("generated_at", t("cover_generated_at"), "date")}</div>
            <div>{field("vat", t("cover_vat"), "text", { placeholder: "12345678901" })}</div>
            <div>{field("cf", t("cover_cf"), "text")}</div>
            <div>{field("cciaa", t("cover_cciaa"), "text", { placeholder: "MI-1234567" })}</div>
            <div>{field("phone", t("cover_phone"), "tel", { placeholder: "+39 02 1234567" })}</div>
            <div className="sm:col-span-2">{field("address", t("cover_address"), "text")}</div>
            <div>{field("email", t("cover_email"), "email")}</div>
            <div>{field("website", t("cover_website"), "url", { placeholder: "www.esempio.it" })}</div>
          </div>
        </>
      )}
    </div>
  );
}
